import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Typography, Button, Card, Stack, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Switch, FormControlLabel, Divider, Grid,
} from '@mui/material'
import {
  Add, Edit, Delete, Business, Phone, Email, ContentCopy,
  Palette, Visibility, VisibilityOff,
} from '@mui/icons-material'
import {
  useCustomers, useCreateCustomer, useUpdateCustomer, useDeleteCustomer,
  usePortalConfig, useUpdatePortalConfig, useFeedback,
  Customer, CustomerPortalConfig,
} from '../../hooks/useWave2'
import { ConfirmDialog } from '../../components/ConfirmDialog'

export default function CustomersPage() {
  const { t } = useTranslation()
  const customers = useCustomers()
  const create = useCreateCustomer()
  const update = useUpdateCustomer()
  const remove = useDeleteCustomer()
  const portal = usePortalConfig()
  const updatePortal = useUpdatePortalConfig()
  const feedback = useFeedback()

  const [editing, setEditing] = useState<Customer | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Customer | null>(null)

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#0f172a">
            {t('customers.title', 'Customers')}
          </Typography>
          <Typography variant="caption" color="#64748b">
            {t('customers.subtitle', 'Your B2B customers, branded portal, ratings.')}
          </Typography>
        </Box>
        <Button startIcon={<Add />} variant="contained" onClick={() => setCreating(true)}>
          {t('customers.add', 'Add Customer')}
        </Button>
      </Stack>

      {/* Portal config */}
      <Card sx={{ p: 2.5, mb: 3, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
          <Palette sx={{ verticalAlign: 'middle', mr: 1, fontSize: 20 }} />
          {t('customers.portal_config', 'Customer Portal Branding')}
        </Typography>
        <Typography variant="caption" color="#64748b" sx={{ mb: 2, display: 'block' }}>
          {t('customers.portal_hint', 'How your customers see live tracking, ETAs, and POD.')}
        </Typography>
        {portal.isLoading ? (
          <CircularProgress size={20} />
        ) : portal.data ? (
          <PortalConfigForm cfg={portal.data} onSave={(input) => updatePortal.mutate(input)} />
        ) : null}
      </Card>

      {/* Customers list */}
      <Card sx={{ p: 0, borderRadius: 2.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {customers.isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : customers.data && customers.data.length > 0 ? (
          customers.data.map((c, i) => (
            <Box
              key={c.id}
              sx={{
                p: 2, display: 'flex', alignItems: 'center', gap: 2,
                borderBottom: i < (customers.data?.length ?? 0) - 1 ? '1px solid #f1f5f9' : 'none',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <Business sx={{ color: '#7c3aed' }} />
              <Box sx={{ flex: 1 }}>
                <Typography fontWeight={600} color="#0f172a">{c.name}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                  {c.contact_phone && <Chip size="small" icon={<Phone sx={{ fontSize: 12 }} />} label={c.contact_phone} />}
                  {c.contact_email && <Chip size="small" icon={<Email sx={{ fontSize: 12 }} />} label={c.contact_email} />}
                  <Chip size="small" label={c.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                        color={c.is_active ? 'success' : 'default'} />
                </Stack>
              </Box>
              <Tooltip title={t('common.edit', 'Edit')}>
                <IconButton onClick={() => setEditing(c)} size="small"><Edit fontSize="small" /></IconButton>
              </Tooltip>
              <Tooltip title={t('common.delete', 'Delete')}>
                <IconButton onClick={() => setDeleting(c)} size="small" color="error"><Delete fontSize="small" /></IconButton>
              </Tooltip>
            </Box>
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', color: '#64748b' }}>
            {t('customers.empty', 'No customers yet — add one to get started.')}
          </Box>
        )}
      </Card>

      {/* Recent feedback */}
      {feedback.data && feedback.data.length > 0 && (
        <Card sx={{ p: 2.5, mt: 3, borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            {t('customers.recent_feedback', 'Recent Feedback')}
          </Typography>
          <Stack spacing={1}>
            {feedback.data.slice(0, 5).map((f) => (
              <Box key={f.id} sx={{ p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={700}>{'★'.repeat(f.rating)}{'☆'.repeat(5 - f.rating)}</Typography>
                  <Typography variant="caption" color="#94a3b8">
                    {new Date(f.submitted_at).toLocaleString()}
                  </Typography>
                </Stack>
                {f.comment && <Typography variant="body2" sx={{ mt: 0.5 }}>{f.comment}</Typography>}
              </Box>
            ))}
          </Stack>
        </Card>
      )}

      {(editing || creating) && (
        <CustomerDialog
          customer={editing}
          onClose={() => { setEditing(null); setCreating(false) }}
          onSave={async (data) => {
            if (editing) await update.mutateAsync({ id: editing.id, ...data })
            else await create.mutateAsync(data)
            setEditing(null); setCreating(false)
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          open
          title={t('customers.delete_title', 'Delete customer?')}
          message={t('customers.delete_message', { name: deleting.name, defaultValue: `Delete ${deleting.name}?` })}
          onConfirm={async () => { await remove.mutateAsync(deleting.id); setDeleting(null) }}
          onClose={() => setDeleting(null)}
        />
      )}
    </Box>
  )
}

function PortalConfigForm({ cfg, onSave }: { cfg: CustomerPortalConfig; onSave: (cfg: Partial<CustomerPortalConfig>) => void }) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(cfg)
  const update = (k: keyof CustomerPortalConfig, v: unknown) => setDraft((d) => ({ ...d, [k]: v }))

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label={t('customers.portal_slug', 'Portal slug')}
                     value={draft.portal_slug ?? ''} onChange={(e) => update('portal_slug', e.target.value)}
                     helperText={t('customers.portal_slug_hint', 'e.g. acme — track.omnitrace.app/acme')}
                     InputProps={{ endAdornment: draft.portal_slug ? <IconButton size="small"
                       onClick={() => navigator.clipboard.writeText(`https://track.omnitrace.app/${draft.portal_slug}`)}>
                       <ContentCopy fontSize="small" /></IconButton> : null }} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label={t('customers.logo_url', 'Logo URL')}
                     value={draft.logo_url ?? ''} onChange={(e) => update('logo_url', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth size="small" type="color" label={t('customers.brand_color', 'Brand color')}
                     value={draft.brand_color ?? '#7c3aed'} onChange={(e) => update('brand_color', e.target.value)} />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField fullWidth size="small" type="color" label={t('customers.accent_color', 'Accent color')}
                     value={draft.accent_color ?? '#f59e0b'} onChange={(e) => update('accent_color', e.target.value)} />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField fullWidth size="small" label={t('customers.welcome_message', 'Welcome message')}
                     value={draft.welcome_message ?? ''} onChange={(e) => update('welcome_message', e.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <FormControlLabel control={<Switch checked={draft.show_eta} onChange={(e) => update('show_eta', e.target.checked)} />}
                              label={t('customers.show_eta', 'Show ETA')} />
            <FormControlLabel control={<Switch checked={draft.show_driver_name} onChange={(e) => update('show_driver_name', e.target.checked)} />}
                              label={t('customers.show_driver', 'Show driver name')} />
            <FormControlLabel control={<Switch checked={draft.allow_reschedule} onChange={(e) => update('allow_reschedule', e.target.checked)} />}
                              label={t('customers.allow_reschedule', 'Allow reschedule')} />
            <FormControlLabel control={<Switch checked={draft.allow_feedback} onChange={(e) => update('allow_feedback', e.target.checked)} />}
                              label={t('customers.allow_feedback', 'Allow feedback')} />
            <FormControlLabel control={<Switch checked={draft.is_published} onChange={(e) => update('is_published', e.target.checked)} />}
                              label={
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                  {draft.is_published ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                  <span>{t('customers.published', 'Published')}</span>
                                </Stack>
                              } />
          </Stack>
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={() => setDraft(cfg)}>{t('common.reset', 'Reset')}</Button>
        <Button variant="contained" onClick={() => onSave(draft)}>{t('common.save', 'Save')}</Button>
      </Stack>
    </Box>
  )
}

function CustomerDialog({ customer, onClose, onSave }: {
  customer: Customer | null; onClose: () => void; onSave: (data: Partial<Customer>) => Promise<void>
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<Partial<Customer>>(
    customer ?? { name: '', contact_name: '', contact_phone: '', contact_email: '', is_active: true }
  )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{customer ? t('customers.edit', 'Edit Customer') : t('customers.add', 'Add Customer')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label={t('common.name', 'Name')} value={form.name ?? ''}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
          <TextField label={t('customers.contact_name', 'Contact name')} value={form.contact_name ?? ''}
                     onChange={(e) => setForm({ ...form, contact_name: e.target.value })} fullWidth />
          <TextField label={t('customers.contact_phone', 'Phone')} value={form.contact_phone ?? ''}
                     onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} fullWidth />
          <TextField label={t('customers.contact_email', 'Email')} value={form.contact_email ?? ''}
                     onChange={(e) => setForm({ ...form, contact_email: e.target.value })} fullWidth />
          <TextField label={t('customers.billing_address', 'Billing address')} value={form.billing_address ?? ''}
                     onChange={(e) => setForm({ ...form, billing_address: e.target.value })} multiline rows={2} fullWidth />
          <TextField label={t('customers.tax_id', 'Tax ID')} value={form.tax_id ?? ''}
                     onChange={(e) => setForm({ ...form, tax_id: e.target.value })} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button variant="contained" disabled={!form.name} onClick={async () => { await onSave(form) }}>
          {t('common.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
